import React, { useEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import styles from "./CascadingDropdown.module.scss";
import { ReactComponent as ArrowNextIcon } from "../../icons/arrow-next.svg";
import { ReactComponent as DownArrowIcon } from "../../icons/down-arrow.svg";
import { ReactComponent as UpArrowIcon } from "../../icons/up-arrow.svg";

const CascadingDropdown = ({
  label,
  text,
  field,
  form,
  className,
  disabled,
  options = [],
  suboptions = [],
  selectedValues = [],
  disableSelected = false,
  initialHoveredOption = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [lockedOption, setLockedOption] = useState(null);
  const optionsRef = useRef({});

  useEffect(() => {
    if (!isOpen || !initialHoveredOption) return;

    // Scroll only once *on open*, not on every hover
    const timeout = setTimeout(() => {
      const el = optionsRef.current[initialHoveredOption];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50); // slight delay to ensure render

    return () => clearTimeout(timeout);
  }, [isOpen, initialHoveredOption]);

  useEffect(() => {
    if (initialHoveredOption) {
      setHoveredOption(initialHoveredOption);
    }
  }, [initialHoveredOption]);

  useEffect(() => {
    if (!isOpen) {
      // Reset locked option when dropdown closes
      setLockedOption(null);
      return;
    }

    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (key.length === 1 && key.match(/[a-z]/)) {
        const match = options.find((opt) =>
          opt.label.toLowerCase().startsWith(key),
        );
        if (match) {
          setHoveredOption(match.id);
          const el = optionsRef.current[match.id];
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, options]);

  const selectedOptionLabel =
    suboptions.find((sub) => sub.id === field.value)?.label || text;

  const optionToSuboptions = options.reduce((acc, option) => {
    acc[option.id] = suboptions.filter((sub) =>
      sub.reference.startsWith(option.id),
    );
    return acc;
  }, {});

  const handleSelect = (option) => {
    form.setFieldValue(field.name, option.value);
    setIsOpen(false);
    setLockedOption(null);
  };

  const handleOptionClick = (optionId) => {
    if (lockedOption === optionId) {
      // If clicking on already locked option, unlock it
      setLockedOption(null);
    } else {
      // Lock the clicked option
      setLockedOption(optionId);
      setHoveredOption(optionId);
    }
  };

  const handleOptionHover = (optionId) => {
    // Only update hover if no option is locked
    if (lockedOption === null) {
      setHoveredOption(optionId);
    }
  };

  // Use locked option if available, otherwise use hovered option
  const activeOption = lockedOption || hoveredOption;

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className={`${styles.dropdownContainer} ${className || ""}`}>
        {label && <label>{label}</label>}
        <div
          className={`${styles.dropdownTrigger} ${disabled ? styles.disabled : ""} ${isOpen && styles.active}`}
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => {
                const next = !prev;
                if (next && initialHoveredOption) {
                  setHoveredOption(initialHoveredOption);
                }
                return next;
              });
            }
          }}
        >
          {selectedOptionLabel}
          <span className={styles.icon}>
            {isOpen ? <UpArrowIcon /> : <DownArrowIcon />}
          </span>
        </div>

        {isOpen && (
          <div className={styles.dropdownList}>
            <div
              className={`${styles.optionsColumn} ${
                activeOption ? styles.active : ""
              }`}
            >
              {options.map((option) => {
                const isLocked = lockedOption === option.id;
                const isHovered = hoveredOption === option.id;

                return (
                  <div
                    key={option.id}
                    ref={(el) => (optionsRef.current[option.id] = el)}
                    className={`${styles.dropdownItem} ${
                      isHovered ? styles.highlighted : ""
                    } ${isLocked ? styles.locked : ""}`}
                    onMouseEnter={() => handleOptionHover(option.id)}
                    onClick={() => handleOptionClick(option.id)}
                  >
                    {option.label}
                    <ArrowNextIcon />
                  </div>
                );
              })}
            </div>
            {activeOption && (
              <div
                className={`${styles.suboptionsColumn} ${
                  activeOption ? styles.active : ""
                }`}
              >
                {optionToSuboptions[activeOption]?.map((sub) => {
                  const isDisabled =
                    disableSelected && selectedValues.includes(sub.id);

                  return (
                    <div
                      key={sub.id}
                      className={`${styles.subDropdownItem} ${
                        isDisabled ? styles.disabledItem : ""
                      }`}
                      onClick={() =>
                        !isDisabled &&
                        handleSelect({ value: sub.id, label: sub.label })
                      }
                    >
                      {sub.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default CascadingDropdown;
